/**
 * client/src/cma/ReportProof.tsx — the finished report, page by page, as it will actually print.
 *
 * WHY THIS EXISTS  (owner, 2026-08-31: "we need to do a better job when on the screen that focuses
 * on edits of the CMA or AMB ... allows the user to see page by page what the report will look
 * like, maybe there are 2 views so we dont break everything").
 *
 * WHAT THE SCREEN OFFERED BEFORE. `ReportBody` renders the report's CONTENT — the same numbers,
 * the same prose, the same sections — as web blocks in the app's own type. It is a good reading
 * view and it is not a proof: it cannot show where a page breaks, whether the cover carries the
 * firm's mark, whether a comp grid is torn across two sheets, or what an agent's client will
 * actually open. Those are exactly the things someone checks before putting their name on a
 * document, and the only way to check them was to download the file and open it elsewhere.
 *
 * WHAT THIS SHOWS INSTEAD, AND WHY IT CANNOT LIE. It is not a re-implementation of the renderer.
 * It fetches the REAL PDF from the same `/api/amb/jobs/{id}/download` endpoint the Download button
 * uses, with the same layout, and displays those bytes. So the preview and the deliverable are the
 * same artifact by construction — there is no second rendering path that could drift from the
 * first, which is the failure this product cannot afford anywhere and least of all here.
 *
 * WHY THE BROWSER'S OWN VIEWER, AND NOT A CANVAS RENDERER. Page-by-page scrolling, the page
 * counter, zoom, keyboard paging and text search all already exist in every browser's built-in PDF
 * viewer, and using it costs no dependency. The alternative — pdf.js painting each page onto a
 * canvas — buys a custom page rail at the price of a large runtime dependency, a worker bundle,
 * and a SECOND rendering of the document that can disagree with the first about pagination. That
 * is the wrong trade for a screen whose entire job is to be trustworthy about what will print.
 *
 * THE AS-OF LINE IS NOT DECORATION. The PDF is rendered when this view opens; an edit saved after
 * that is not in the bytes on screen until it re-renders. So the toolbar states the moment these
 * pages were built and offers a refresh, rather than letting a stale document quietly pass for a
 * current one. `ReportEditor`'s `onSaved` triggers the refresh automatically; the button is for
 * everything else.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { themeVar as colors } from "../design-tokens";
import { downloadUrl } from "./api";

export interface ReportProofProps {
  jobId: string;
  /** The layout the agent has chosen, so the pages match what the download will give them. */
  layout?: string;
  /** Bumped by the caller after a save, to re-render the document. */
  reloadKey?: number;
  /** Rendered in the toolbar's right end — the caller's own download and edit controls. */
  actions?: React.ReactNode;
}

type State =
  | { kind: "loading" }
  | { kind: "ready"; url: string; at: Date }
  | { kind: "error"; message: string };

export function ReportProof({ jobId, layout, reloadKey = 0, actions }: ReportProofProps) {
  const [state, setState] = useState<State>({ kind: "loading" });
  // The object URL currently held open. Revoked on every replacement and on unmount — a blob URL
  // pins its bytes in memory until it is revoked, and a report PDF is not small.
  const urlRef = useRef<string | null>(null);

  const render = useCallback(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    // `credentials: "same-origin"` because the download route is session-authed like every other
    // route in this app. Fetched rather than pointed at directly so a 401 or a 500 becomes a
    // sentence on screen instead of the browser's own error page inside our frame.
    fetch(downloadUrl(jobId, "pdf", layout), { credentials: "same-origin" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`The report renderer answered ${r.status}.`);
        const blob = await r.blob();
        if (cancelled) return;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setState({ kind: "ready", url, at: new Date() });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({ kind: "error", message: e instanceof Error ? e.message : "Couldn't build the pages." });
      });
    return () => {
      cancelled = true;
    };
  }, [jobId, layout]);

  useEffect(() => render(), [render, reloadKey]);

  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
  }, []);

  return (
    <section className="vb-proof" aria-label="The report as it will print">
      <div className="vb-proof-bar">
        <div className="vb-proof-status">
          {state.kind === "loading" && <span>Building the pages…</span>}
          {state.kind === "ready" && (
            <>
              {/* WHICH VERSION THESE PAGES ARE. Said plainly, because an edit saved after this
                  rendered is not in them, and a preview that quietly aged would be the screen
                  making a claim it cannot support. */}
              <span>Built at {state.at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} — an
                edit saved after that is not in these pages yet.</span>
              <button type="button" className="vb-proof-refresh" onClick={() => render()}>
                Rebuild
              </button>
            </>
          )}
          {state.kind === "error" && (
            <span role="alert" style={{ color: colors.caution }}>
              {state.message} The download buttons still work — they use the same renderer.
            </span>
          )}
        </div>
        {actions && <div className="vb-proof-actions">{actions}</div>}
      </div>

      {state.kind === "ready" && (
        // `title` is what a screen reader announces for the frame, and it is also the only thing
        // that distinguishes it from the app around it in an accessibility tree.
        <iframe className="vb-proof-frame" src={state.url} title="The report, page by page" />
      )}
      {state.kind === "loading" && <div className="vb-proof-frame vb-proof-skeleton" aria-hidden="true" />}
    </section>
  );
}
